/* eslint-disable tree-shaking/no-side-effects-in-initialization -- declared side-effect in package.json */

import { FunctionKeys } from "@hex/common";

/*
Purpose of this file is to create a "pushstate" and "replacestate" event
that is triggered to round out the tracking of window history changes, in
addition to the existing "popstate" event.
*/

const patchHistoryMethod = <TEvent extends HistoryStateEvent>(
  method: Extract<FunctionKeys<History>, "pushState" | "replaceState">,
  eventName: string,
): void => {
  const original = window.history[method];

  window.history[method] = function (...args: Parameters<typeof original>) {
    const result = original.apply(this, args);
    const event = new Event(eventName) as TEvent;
    // @ts-expect-error readonly but we're the makers
    event.state = args[0];

    window.dispatchEvent(event);

    return result;
  };
};

patchHistoryMethod<PushStateEvent>("pushState", "pushstate");
patchHistoryMethod<ReplaceStateEvent>("replaceState", "replacestate");

/*
Mimic the typing behind popstate.
*/

export interface HistoryStateEvent extends Event {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- state is any
  readonly state: any;
}

export interface PushStateEvent extends HistoryStateEvent {}
export interface ReplaceStateEvent extends HistoryStateEvent {}

declare global {
  interface WindowEventMap {
    pushstate: PushStateEvent;
    replacestate: ReplaceStateEvent;
  }
}

/*
Trigger full reload since we can't really "undo/redo" the patch.
*/

if (module.hot) {
  module.hot.decline();
}

declare global {
  namespace NodeJS {
    interface Module {
      /** @see https://webpack.js.org/api/hot-module-replacement#module-api */
      hot?: {
        /** @see https://webpack.js.org/api/hot-module-replacement#decline-self */
        decline: () => void;
      };
    }
  }
}
